import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectChange } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AssetData,
  PrintAttributeStatus,
  PrintAttributeType,
} from '../../../models/print-attribute.model';
import {
  EventPrintAttributeDefinition,
  EventPrintAttributeSavePayload,
} from '../../../models/event-print-attribute.model';
import { combineLatest, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import * as dayjs from 'dayjs';
import {
  CopyFromEventSourceItem,
  EventPrintAttributeService,
} from '../../../services/event-print-attribute.service';
import { PrintAttributeService } from '../../../services/print-attribute.service';
import { OfferPromoService } from '../../../services/offerpromo.service';
import { OfferPromo } from '../../../models/offer-promo.model';
import { PromoDomain } from '../../../models/promo-domain.model';
import { EventTypeService } from '../../../services/event-type.service';
import { DivisionService } from '../../../services/division.service';
import { DEFAULT_PAGING, DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { EventService } from '../../../services/event.service';
import { AssetsSearchDialogComponent } from '../../assets-search-dialog/assets-search-dialog.component';
import { SearchAsset } from '../../../models/search-state.model';
import { VersionService } from '../../../services/version.service';
import { Version } from '../../../models/version.model';
import { ConfirmDialogComponent } from 'src/app/@shared';
import { IPrintPage } from '../../../models/print-page.model';
import { UploadAssetsEventsComponent } from '../../upload-assets-events/upload-assets-events.component';
import { SelectionModel } from '@angular/cdk/collections';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';

interface DivisionOption {
  id: string;
  name: string;
}

interface DynamicField {
  id: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'url' | 'textarea';
  value?: string;
  placeholder?: string;
  readOnly?: boolean;
}

interface ImportTemplateRow {
  id: string;
  name: string;
  attrType: string;
}

@Component({
  selector: 'app-create-event-attribute',
  templateUrl: './create-event-attribute.component.html',
  styleUrls: ['./create-event-attribute.component.scss'],
})
export class CreateEventAttributeComponent implements OnInit, OnDestroy {
  readonly AttributeStatuses = PrintAttributeStatus;
  addMode: 'create' | 'import' | 'copy' = 'create';
  importSearch = '';
  copySearchControl = new FormControl('');
  /** Filters copy-from-event attribute list via {@link EventPrintAttributeService.ByNotEventIdSearch}. */
  copyAttributesSearchControl = new FormControl('');
  readonly selectionTableColumns = ['select', 'Name', 'Type'];
  importSelection = new SelectionModel<any>(true, []);
  copyAttributesList: EventPrintAttributeDefinition[] = [];
  templatesList: EventPrintAttributeDefinition[] = [];

  copyViewModel$ = combineLatest([
    this.eventPrintAttributeService.ByNotEventIdAttributes$,
    this.eventPrintAttributeService.ByNotEventIdTotalRecords$,
    this.eventPrintAttributeService.ByNotEventIdPage$,
  ]).pipe(
    map(([attributesFromSourceEvent, totalRecords, page]) => {
      this.copyAttributesList = attributesFromSourceEvent;
      return { attributesFromSourceEvent, totalRecords, page };
    }),
  );

  importViewModel$ = combineLatest([
    this.eventPrintAttributeService.importFromTemplateAttributes$,
    this.eventPrintAttributeService.importFromTemplatePage$,
    this.eventPrintAttributeService.importFromTemplateTotalRecords$,
  ]).pipe(
    map(([attributesFromTemplate, importFromTemplatePage, totalRecords]) => {
      this.templatesList = attributesFromTemplate;
      return { attributesFromTemplate, importFromTemplatePage, totalRecords };
    }),
  );
  copyEventOptions: Array<{
    id: string;
    name: string;
    selected: boolean;
    divisionNames?: string;
    createdAt?: string;
    attributes: Array<{
      id: string;
      name: string;
      type: string;
      divisionNames?: string;
      selected: boolean;
    }>;
  }> = [];

  /** Same `Detail.Data` keys as {@link CreatePrintAttributeComponent}. */
  private readonly dynamicFieldDefs: { [key: string]: DynamicField[] } = {
    address: [{ id: 'StoreAddress', label: 'Store Address', type: 'text' }],
    sale_dates: [
      { id: 'SaleStartDate', label: 'Sale Start Date', type: 'date' },
      { id: 'SaleEndDate', label: 'Sale End Date', type: 'date' },
    ],
    url: [
      { id: 'Url', label: 'URL', type: 'url', placeholder: 'https://example.com' },
    ],
    printer_code: [
      { id: 'PrinterCode', label: 'Printer Code', type: 'text' },
      { id: 'RoutingDestination', label: 'Routing Destination', type: 'text' },
    ],
    header: [
      { id: 'HeaderText', label: 'Header Text', type: 'text' },
      { id: 'SubheaderText', label: 'Subheader Text', type: 'text' },
    ],
    creative: [
      { id: 'CreativeAssetId', label: 'Asset ID', type: 'text' },
      { id: 'PlacementNotes', label: 'Placement Notes', type: 'text' },
    ],
    market_place: [
      { id: 'MarketplaceName', label: 'Marketplace Name', type: 'text' },
      { id: 'PromoBlockContent', label: 'Promo Block Content', type: 'text' },
    ],
    offer_event: [
      { id: 'EventLabel', label: 'Event Label', type: 'text' },
      { id: 'BrandOverlayText', label: 'Brand Overlay Text', type: 'text' },
    ],
    abev: [
      { id: 'LicenseType', label: 'License Type', type: 'text' },
      { id: 'AbevLayoutNotes', label: 'ABEV Layout Notes', type: 'text' },
    ],
    dry_version: [
      { id: 'ReplacementContent', label: 'Replacement Content', type: 'text' },
      { id: 'DryVersionLayoutNotes', label: 'Layout Notes', type: 'text' },
    ],
  };

  /**
   * Sale window: start must be on or before end (same calendar day is allowed).
   */
  private readonly saleDatesRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const g = group as FormGroup;
    const rawStart = g.get('SaleStartDate')?.value;
    const rawEnd = g.get('SaleEndDate')?.value;
    if (rawStart == null || rawEnd == null || rawStart === '' || rawEnd === '') {
      return null;
    }
    const start = dayjs(rawStart instanceof Date ? rawStart : String(rawStart)).startOf('day');
    const end = dayjs(rawEnd instanceof Date ? rawEnd : String(rawEnd)).startOf('day');
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    return start.isAfter(end, 'day') ? { saleDateRange: true } : null;
  };

  dynamicFields: DynamicField[] = [];
  private destroy$ = new Subject<void>();
  eventId = '0';
  eventAttributeId = '0';
  isEditMode = false;

  attributeTypeOptions: { id: string; label: string }[] = [];
  imageRequiredTypes: string[] = [
    PrintAttributeType.url,
    PrintAttributeType.creative,
    PrintAttributeType.header,
    PrintAttributeType.market_place,
    PrintAttributeType.abev,
  ];
  showImageUpload = false;
  selectedAttributeType = '';
  selectedAssetPreview: AssetData | null = null;
  previewImageLoadFailed = false;
  divisionOptions: DivisionOption[] = [];
  eventTypeOptions: DivisionOption[] = [];
  availableVersions: Version[] = [];
  private previousDivisionSelection: string[] = [];
  readOnlyMode = false;

  /** `Detail.Data` form keys using `mat-datepicker` (same format as print-attribute on save). */
  private dateDataFieldIds: string[] = [];

  printPagesList: IPrintPage[] = [];

  formGroup!: FormGroup;
  sourceEvent: any;
  attributesFromSourceEvent: any;
  selection = new SelectionModel<EventPrintAttributeDefinition>(
    true,
    [],
    true,
    (a, b) => a.DomainId === b.DomainId,
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private eventPrintAttributeService: EventPrintAttributeService,
    private printAttributeService: PrintAttributeService,
    private offerPromoService: OfferPromoService<OfferPromo, PromoDomain<OfferPromo>>,
    private eventTypeService: EventTypeService<any>,
    private divisionService: DivisionService<any>,
    private dialog: MatDialog,
    private eventService: EventService<any>,
    private versionService: VersionService<Version>,
    private authorizationService: AuthorizationService,
    @Optional() private dialogRef?: MatDialogRef<CreateEventAttributeComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { eventId?: string; eventAttributeId?: string },
  ) {
    this.printAttributeService.printAttributeTypes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((types) => {
        this.attributeTypeOptions = types;
      });

    this.formGroup = this.fb.group({
      Detail: this.fb.group({
        Id: [''],
        ClientKey: ['', [Validators.required]],
        UserId: [''],
        PrintAttributeName: ['', [Validators.required]],
        PrintAttributeType: ['', [Validators.required]],
        Description: [''],
        Status: [PrintAttributeStatus.Active, [Validators.required]],
        LogicalVersions: [''],
        LogicalPrintPage: [''],
        CreatedAt: [''],
        EventOnlyAttribute: [true],
      }),
      Data: this.fb.group({}),
      DivisionIds: [[], [Validators.required]],
      DivisionNames: [''],
      EventTypeName: [''],
      AssetId: [''],
      EventTypeId: ['', [Validators.required]],
      BannerId: [''],
      BannerName: [''],
      PrintPageId: [''],
      Position: [null as number | null],
      VersionIds: [[]],
    });

    this.detailForm
      .get('PrintAttributeType')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe((attributeType) => {
        this.selectedAttributeType = attributeType || '';
        this.showImageUpload = this.isAssetApplicableType(attributeType);
        if (!this.showImageUpload) {
          this.removeSelectedAsset();
        }
        this.renderDynamicFields(attributeType || '');
        this.updateAssetRequirementValidators();
      });
  }

  get detailForm(): FormGroup {
    return this.formGroup.get('Detail') as FormGroup;
  }

  get dataFieldsForm(): FormGroup {
    return this.formGroup.get('Data') as FormGroup;
  }

  ngOnInit(): void {
    this.eventId =
      this.dialogData?.eventId ?? this.route.snapshot.paramMap.get('eventId') ?? '0';
    this.eventAttributeId =
      this.dialogData?.eventAttributeId ??
      this.route.snapshot.paramMap.get('eventAttributeId') ??
      '0';
    this.isEditMode = this.eventAttributeId !== '0';
    if (this.isEditMode) {
      this.addMode = 'create';
    }

    this.loadLookups();
    this.loadPrintPagesForEvent();
    this.loadEventVersions();
    this.setupReactiveBehaviors();
    this.applyEventDefaults();

    if (!this.isEditMode) {
      this.clearEventAttributeEditFieldLocks();
    }

    if (this.isEditMode) {
      this.loadExistingRecord();
    }

    this.eventPrintAttributeService.resetCopyImportModalState();
    this.eventPrintAttributeService.targetEventId = this.eventId;

    this.copyAttributesSearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        const q = String(term ?? '').trim();
        this.eventPrintAttributeService.ByNotEventIdSearch = q;
        this.eventPrintAttributeService.byNotEventIdPage({
          ...this.eventPrintAttributeService.ByNotEventIdPage,
          pageIndex: 0,
        });
      });
      this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
      this.canEdit();
  }

  ngOnDestroy(): void {
    this.resetModalState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetModalState(): void {
    this.eventPrintAttributeService.resetCopyImportModalState();
    this.selection.clear();
    this.importSelection.clear();
    this.sourceEvent = undefined;
    this.importSearch = '';
    this.copySearchControl.reset('', { emitEvent: false });
    this.copyAttributesSearchControl.reset('', { emitEvent: false });
    this.copyAttributesList = [];
    this.templatesList = [];
    if (!this.isEditMode) {
      this.addMode = 'create';
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
      return;
    }

    this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  copyAll(): void {
    this.eventPrintAttributeService.copyAllAttributesFromEvent(this.sourceEvent.DomainId, this.eventId).pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.eventPrintAttributeService.reload();
      this.snackBar.open('Attributes copied from event', 'OK', DEFAULT_SNACKBAR_CONFIG);
      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    });
  }

  save(): void {
    if (this.addMode === 'create' && this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snackBar.open('Please fix all errors before saving', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    if (this.addMode === 'import') {
      const selectedTemplateIds = this.importSelection.selected.map((x) => x.DomainId);
      if (!selectedTemplateIds.length) {
        this.snackBar.open('Select at least one template to import.', 'OK', DEFAULT_SNACKBAR_CONFIG);
        return;
      }
      this.eventPrintAttributeService
        .importPrintAttributes(this.eventId, selectedTemplateIds)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          this.eventPrintAttributeService.reload();
          this.snackBar.open('Attributes imported', 'OK', DEFAULT_SNACKBAR_CONFIG);
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        });
      return;
    }

    if (this.addMode === 'copy') {
      if (!this.sourceEvent?.DomainId) {
        this.snackBar.open('Select a source event first.', 'OK', DEFAULT_SNACKBAR_CONFIG);
        return;
      }
      if (!this.selection.selected.length) {
        this.snackBar.open('Select at least one attribute to copy.', 'OK', DEFAULT_SNACKBAR_CONFIG);
        return;
      }

      this.eventPrintAttributeService
        .copyFromEvent(
          this.sourceEvent.DomainId,
          this.eventId,
          this.selection.selected
            .map((a) => a.DomainId)
            .filter((id): id is string => !!id),
        )
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          this.eventPrintAttributeService.reload();
          this.snackBar.open('Attributes copied from event', 'OK', DEFAULT_SNACKBAR_CONFIG);
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        });
      return;
    }

    if(this.readOnlyMode) {
      return;
    }

    const payload = this.buildPayload();
    const request$ = this.isEditMode
      ? this.eventPrintAttributeService.updateEventPrintAttribute(this.eventAttributeId, payload)
      : this.eventPrintAttributeService.createEventPrintAttribute(payload);

    request$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.eventPrintAttributeService.reload();
        this.snackBar.open(
          this.isEditMode ? 'Event attribute updated' : 'Event attribute created',
          'OK',
          DEFAULT_SNACKBAR_CONFIG,
        );

        if (this.dialogRef) {
          this.dialogRef.close(true);
          return;
        }

        this.router.navigate(['../'], {
          relativeTo: this.route,
          queryParamsHandling: 'preserve',
        });
      },
      error: (err) =>
        {
        const errorMsg = Array.isArray(err.error) ? err.error.map((e: any) => e.ErrorMessage).join(', ') : err.error?.message ?? 'Could not save event attribute';
        this.snackBar.open(
          errorMsg ?? 'Could not save event attribute',
            'OK',
            DEFAULT_SNACKBAR_CONFIG,
          );
        },
    });
  }

  deleteAttribute(): void {
    if (!this.isEditMode || !this.eventAttributeId || this.eventAttributeId === '0') {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: 'Do you want to remove this Event Attribute?',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe((result) => {
      if (result !== true) {
        return;
      }

      this.eventPrintAttributeService
        .deleteEventPrintAttribute(this.eventAttributeId, this.eventId)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          this.eventPrintAttributeService.reload();
          this.snackBar.open('Event attribute deleted', 'OK', DEFAULT_SNACKBAR_CONFIG);

          if (this.dialogRef) {
            this.dialogRef.close(true);
            return;
          }

          this.router.navigate(['../'], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve',
          });
        });
    });
  }

  getDynamicFieldControl(fieldId: string): FormControl {
    return this.dataFieldsForm.get(fieldId) as FormControl;
  }

  formatDate(date: Date | string | null | undefined): string {
    if (date) {
      return dayjs(date).format('MM/DD/YYYY');
    }
    return '';
  }

  /** `Detail.CreatedAt` sent on save (always `MM/DD/YYYY`). */
  private formatDetailCreatedAtForPayload(value: unknown): string {
    if (value != null && String(value).trim() !== '') {
      const parsed = value instanceof Date ? dayjs(value) : dayjs(String(value).trim());
      if (parsed.isValid()) {
        return parsed.format('MM/DD/YYYY');
      }
    }
    return dayjs().format('MM/DD/YYYY');
  }

  private parseRecordDataForForm(record: EventPrintAttributeDefinition): Record<string, unknown> {
    const merged: Record<string, unknown> = {};

    const merge = (raw: unknown): void => {
      if (raw == null) {
        return;
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) {
          return;
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            Object.assign(merged, parsed as Record<string, unknown>);
          }
        } catch {
          // ignore invalid JSON
        }
        return;
      }
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.assign(merged, raw as Record<string, unknown>);
      }
    };

    merge(record.Data);
    merge((record.Detail as { Data?: unknown } | undefined)?.Data);
    return merged;
  }

  private patchDataFieldsFromRecord(record: EventPrintAttributeDefinition): void {
    const dataFields = this.parseRecordDataForForm(record);
    const dataKeyByLower = new Map<string, string>();
    Object.keys(dataFields).forEach((key) => {
      dataKeyByLower.set(key.toLowerCase(), key);
    });

    this.dynamicFields.forEach((field) => {
      const control = this.dataFieldsForm.get(field.id);
      if (!control) {
        return;
      }

      let value = dataFields[field.id];
      if (value === undefined) {
        const matchedKey = dataKeyByLower.get(field.id.toLowerCase());
        if (matchedKey) {
          value = dataFields[matchedKey];
        }
      }
      if (value === undefined) {
        return;
      }

      if (this.dateDataFieldIds.includes(field.id) && value) {
        control.setValue(this.coerceDateForForm(value), { emitEvent: false });
      } else {
        control.setValue(value, { emitEvent: false });
      }
    });
  }

  private coerceDateForForm(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private buildDataForPayload(): Record<string, string> {
    const raw = (this.dataFieldsForm.getRawValue() ?? {}) as Record<string, unknown>;
    const normalized: Record<string, string> = {};

    this.dynamicFields.forEach((field) => {
      const rawValue = raw[field.id];
      if (field.type === 'date') {
        normalized[field.id] = this.formatDate(rawValue as Date | string | null | undefined);
        return;
      }
      if (rawValue == null) {
        normalized[field.id] = '';
        return;
      }
      normalized[field.id] = String(rawValue);
    });

    return normalized;
  }

  private getDivisionNames(divisionIds: string[]): string {
    if (!divisionIds || divisionIds.length === 0) return '';

    if (divisionIds.length === 1 && divisionIds[0] === '__all__') {
      return 'All Divisions';
    }

    const realIds = divisionIds.filter((id) => id !== '__all__');
    const names = realIds
      .map((id) => {
        const division = this.divisionOptions.find((d) => d.id === id);
        return division ? division.name : '';
      })
      .filter((name) => name);
    return names.join(', ');
  }

  private normalizeVersionsForForm(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean).join(', ');
    }
    return String(value).trim();
  }

  private isAssetApplicableType(attributeType: string | null | undefined): boolean {
    if (!attributeType) {
      return false;
    }
    return this.imageRequiredTypes.includes(attributeType);
  }

  private getAssetDataFieldId(): string | null {
    const field = this.dynamicFields.find(
      (f) => f.readOnly && /AssetId$/i.test(f.id),
    );
    return field?.id ?? null;
  }

  get isAssetUploadRequired(): boolean {
    return this.showImageUpload || !!this.getAssetDataFieldId();
  }

  private updateAssetRequirementValidators(): void {
    const assetIdCtrl = this.formGroup.get('AssetId');
    if (!assetIdCtrl) {
      return;
    }
    if (this.isAssetUploadRequired) {
      assetIdCtrl.setValidators([Validators.required]);
    } else {
      assetIdCtrl.clearValidators();
      assetIdCtrl.setValue('', { emitEvent: false });
    }
    assetIdCtrl.updateValueAndValidity({ emitEvent: false });
  }

  onDivisionChange(event: MatSelectChange): void {
    const selectedValues = (event.value || []) as string[];
    const control = this.formGroup.get('DivisionIds');
    if (!control) return;

    const allWasPreviouslySelected = this.previousDivisionSelection.includes('__all__');
    const allIsNowSelected = selectedValues.includes('__all__');
    const individualWasAdded = selectedValues.some(
      (v: string) => v !== '__all__' && !this.previousDivisionSelection.includes(v),
    );

    if (!allWasPreviouslySelected && allIsNowSelected) {
      control.setValue(['__all__'], { emitEvent: false });
      this.previousDivisionSelection = ['__all__'];
    } else if (allWasPreviouslySelected && individualWasAdded) {
      const cleanedValues = selectedValues.filter((v: string) => v !== '__all__');
      control.setValue(cleanedValues, { emitEvent: false });
      this.previousDivisionSelection = cleanedValues;
    } else {
      const cleanedValues = selectedValues.filter((v: string) => v !== '__all__');
      control.setValue(cleanedValues, { emitEvent: false });
      this.previousDivisionSelection = cleanedValues;
    }
  }

  private renderDynamicFields(attributeType: string): void {
    Object.keys(this.dataFieldsForm.controls).forEach((key) => {
      this.dataFieldsForm.removeControl(key);
    });
    this.dataFieldsForm.clearValidators();
    this.dateDataFieldIds = [];

    if (attributeType && this.dynamicFieldDefs[attributeType]) {
      this.dynamicFields = JSON.parse(JSON.stringify(this.dynamicFieldDefs[attributeType]));

      this.dynamicFields.forEach((field) => {
        const validators: ValidatorFn[] = [Validators.required];
        if (field.type === 'url') {
          validators.push(Validators.pattern(/^https?:\/\/.+/));
        }
        if (field.type === 'date') {
          this.dateDataFieldIds.push(field.id);
          const initial = field.value ? this.coerceDateForForm(field.value) : null;
          this.dataFieldsForm.addControl(
            field.id,
            new FormControl<Date | null>(initial, validators),
          );
        } else {
          this.dataFieldsForm.addControl(
            field.id,
            new FormControl(field.value || '', validators),
          );
        }
      });

      if (attributeType === 'sale_dates') {
        this.dataFieldsForm.setValidators(this.saleDatesRangeValidator);
      }
      this.dataFieldsForm.updateValueAndValidity({ emitEvent: false });
    } else {
      this.dynamicFields = [];
      this.dataFieldsForm.updateValueAndValidity({ emitEvent: false });
    }
  }

  private loadLookups(): void {
    this.divisionService.divisionsList$
      .pipe(
        map((divisions) => divisions.map((d) => ({ id: d.Id, name: d.DivisionName }))),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((divisions) => {
        this.divisionOptions = divisions;
      });

    this.eventTypeService.eventTypesList$
      .pipe(
        map((types) => types.map((t) => ({ id: t.Id, name: t.EventTypeName }))),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((types) => {
        this.eventTypeOptions = types;
      });
  }

  private loadExistingRecord(): void {
    this.eventPrintAttributeService
      .getEventPrintAttributeDefinition(this.eventAttributeId, this.eventId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((record) => {
        const detailObj = record.Detail ?? {};

        this.formGroup.patchValue(
          {
            Detail: {
              Id: detailObj.Id ?? '',
              ClientKey: detailObj.ClientKey ?? '',
              UserId: detailObj.UserId ?? '',
              PrintAttributeName: detailObj.PrintAttributeName || '',
              PrintAttributeType: detailObj.PrintAttributeType || '',
              Description: detailObj.Description || '',
              Status: detailObj.Status || PrintAttributeStatus.Active,
              LogicalVersions: this.normalizeVersionsForForm(detailObj.LogicalVersions),
              LogicalPrintPage: detailObj.LogicalPrintPage ?? '',
              CreatedAt: detailObj.CreatedAt ?? '',
              EventOnlyAttribute: detailObj.EventOnlyAttribute ?? false,
            },
            DivisionIds: record.DivisionIds || [],
            DivisionNames: record.DivisionNames || '',
            EventTypeName: record.EventTypeName || '',
            AssetId: record.AssetId || '',
            EventTypeId: record.EventTypeId ?? '',
            BannerId: record.BannerId ?? '',
            BannerName: record.BannerName ?? '',
            PrintPageId: record.PrintPageId || '',
            Position:
              record.Position != null && record.Position !== undefined
                ? Number(record.Position)
                : null,
            VersionIds: record.VersionIds || [],
          },
          { emitEvent: false },
        );

        const attrType = detailObj.PrintAttributeType || '';
        if (attrType) {
          this.selectedAttributeType = attrType;
          this.showImageUpload = this.isAssetApplicableType(attrType);
          this.renderDynamicFields(attrType);
          this.patchDataFieldsFromRecord(record);

          if (this.showImageUpload) {
            this.selectedAssetPreview = (record.AssetData as unknown as AssetData) || null;
          } else {
            this.selectedAssetPreview = null;
            this.previewImageLoadFailed = false;
          }
          this.updateAssetRequirementValidators();
        }

        this.previousDivisionSelection = [...(this.formGroup.get('DivisionIds')?.value || [])];
        this.applyEventAttributeEditFieldLocks();
      });
  }

  private applyEventAttributeEditFieldLocks(): void {
    if(this.readOnlyMode) {
      this.formGroup.disable();
      return;
    }
    if (!this.isEditMode) {
      return;
    }
    this.detailForm.disable({ emitEvent: false });
    this.formGroup.get('DivisionIds')?.disable({ emitEvent: false });
    this.formGroup.get('EventTypeId')?.disable({ emitEvent: false });
    //     this.formGroup.get('VersionIds')?.disable({ emitEvent: false });
    this.formGroup.get('BannerId')?.disable({ emitEvent: false });
    this.formGroup.get('BannerName')?.disable({ emitEvent: false });
    this.formGroup.get('PrintPageId')?.enable({ emitEvent: false });
    this.detailForm.get('Status')?.enable({ emitEvent: false });
    if (this.isEventOnlyAttribute()) {
      this.detailForm.get('PrintAttributeName')?.enable({ emitEvent: false });
      this.detailForm.get('Description')?.enable({ emitEvent: false });
    }
    this.dataFieldsForm.enable({ emitEvent: false });
    this.updatePositionEnabledForPrintPage();
  }

  private isEventOnlyAttribute(): boolean {
    return !!this.detailForm.get('EventOnlyAttribute')?.value;
  }

  private clearEventAttributeEditFieldLocks(): void {
    this.detailForm.enable({ emitEvent: false });
    this.formGroup.get('DivisionIds')?.enable({ emitEvent: false });
    this.formGroup.get('EventTypeId')?.enable({ emitEvent: false });
    // this.formGroup.get('VersionIds')?.enable({ emitEvent: false });
    this.formGroup.get('AssetId')?.enable({ emitEvent: false });
    this.formGroup.get('BannerId')?.enable({ emitEvent: false });
    this.formGroup.get('BannerName')?.enable({ emitEvent: false });
    this.formGroup.get('PrintPageId')?.enable({ emitEvent: false });
    this.dataFieldsForm.enable({ emitEvent: false });
    this.updatePositionEnabledForPrintPage();
  }

  private updatePositionEnabledForPrintPage(): void {
    const printPageCtrl = this.formGroup.get('PrintPageId');
    const positionCtrl = this.formGroup.get('Position');
    const hasPage = !!String(printPageCtrl?.value || '').trim();
    if (!hasPage) {
      positionCtrl?.setValue(null, { emitEvent: false });
      positionCtrl?.disable({ emitEvent: false });
    } else {
      positionCtrl?.enable({ emitEvent: false });
    }
  }

  private buildPayload(): EventPrintAttributeSavePayload {
    const raw = this.formGroup.getRawValue() as {
      Detail: Record<string, unknown>;
      DivisionIds: string[];
      EventTypeId: string;
      AssetId?: string;
      BannerId?: string;
      BannerName?: string;
      PrintPageId?: string;
      Position: number | null;
      VersionIds: string[];
    };

    const divisionIds = [...(raw.DivisionIds || [])];
    const versionIds = [...(raw.VersionIds || [])];
    const logicalVersionNames = this.availableVersions
      .filter((v) => versionIds.includes(v.Id))
      .map((v) => String(v.VersionName || '').trim())
      .filter(Boolean);

    const d = raw.Detail;
    const clientKey = String(d['ClientKey'] ?? '').trim();
    const createdAt = this.formatDetailCreatedAtForPayload(d['CreatedAt']);

    const detailForPayload: EventPrintAttributeSavePayload['Detail'] = {
      Id: String(d['Id'] ?? ''),
      ClientKey: clientKey,
      UserId: String(d['UserId'] ?? ''),
      PrintAttributeName: String(d['PrintAttributeName'] ?? ''),
      PrintAttributeType: String(d['PrintAttributeType'] ?? ''),
      Description: String(d['Description'] ?? ''),
      Status: String(d['Status'] ?? PrintAttributeStatus.Active),
      LogicalVersions: logicalVersionNames,
      LogicalPrintPage: String(d['LogicalPrintPage'] ?? ''),
      CreatedAt: createdAt,
      EventOnlyAttribute: false,
    };

    if(this.isEditMode) { 
      detailForPayload.EventOnlyAttribute = d['EventOnlyAttribute'] ? d['EventOnlyAttribute'] as boolean : false;
    } else {
      detailForPayload.EventOnlyAttribute = true;
    }

    const payload: EventPrintAttributeSavePayload = {
      Detail: detailForPayload,
      DivisionIds: divisionIds,
      DivisionNames: this.getDivisionNames(divisionIds),
      EventTypeName: this.eventTypeOptions.find((e) => e.id === raw.EventTypeId)?.name ?? '',
      EventTypeId: raw.EventTypeId ?? '',
      Data: this.buildDataForPayload(),
      EventId: this.eventId,
      VersionIds: versionIds,
      BannerId: raw.BannerId ?? '',
      PrintPageId: String(raw.PrintPageId || '').trim() || undefined,
      Position:
        raw.Position != null &&
        Number.isFinite(Number(raw.Position)) &&
        Number(raw.Position) >= -1
          ? Number(raw.Position)
          : null,
    };

    const assetId = String(raw.AssetId || this.selectedAssetPreview?.AssetId || '').trim();
    if (this.isAssetUploadRequired && assetId) {
      payload.AssetId = assetId;
      if (this.selectedAssetPreview) {
        payload.AssetData = this.selectedAssetPreview;
      }
    }

    return payload;
  }

  private setupReactiveBehaviors(): void {
    const printPageCtrl = this.formGroup.get('PrintPageId');
    printPageCtrl
      ?.valueChanges.pipe(startWith(printPageCtrl.value), takeUntil(this.destroy$))
      .subscribe(() => this.updatePositionEnabledForPrintPage());
  }

  private loadPrintPagesForEvent(): void {
    if (!this.eventId || this.eventId === '0') {
      return;
    }

    this.offerPromoService
      .GetAllPrintPagesByEventforOffers(this.eventId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((pages) => {
        this.printPagesList = Array.isArray(pages) ? pages : [];
      });
  }

  addAssetsToForm(): void {
    const dialogRef = this.dialog.open(AssetsSearchDialogComponent, {
      width: '100%',
      height: '100%',
      data: {
        isSingleSelect: true,
      },
      disableClose: true,
    });

    dialogRef.componentInstance.emitSlelectedAsset
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((assets: SearchAsset[]) => {
        if (!assets?.length) return;
        const first = assets[0];
        const webUrl = (first.webUrl || '').trim();
        this.formGroup.get('AssetId')?.setValue(first.assetId);
        const assetDataFieldId = this.getAssetDataFieldId();
        if (assetDataFieldId) {
          const assetIdValue = assets.map((a) => a.assetId).join(', ');
          this.dataFieldsForm.get(assetDataFieldId)?.setValue(assetIdValue);
        }

        this.previewImageLoadFailed = false;
        const typeLabel = first.fileType || first.assetType || '';
        this.selectedAssetPreview = {
          AssetId: first.assetId,
          AssetName: first.assetName,
          FileName: first.fileName,
          FileUrl: webUrl,
          FileType: typeLabel,
          InValidFileUrl: null,
          Captions: [
            {
              FileUrl: webUrl,
              LanguageDisplayName: 'ENG',
              Language: 'ENG',
            },
          ],
        };
      });
  }

  removeSelectedAsset(): void {
    this.selectedAssetPreview = null;
    this.previewImageLoadFailed = false;
    this.formGroup.get('AssetId')?.setValue('');
    const assetDataFieldId = this.getAssetDataFieldId();
    if (assetDataFieldId) {
      this.dataFieldsForm.get(assetDataFieldId)?.setValue('');
    }
  }

  onAssetPreviewImageError(): void {
    this.previewImageLoadFailed = true;
  }

  /** Stable accent for placeholder preview when image URL is missing or fails to load. */
  previewAccentFromAssetId(assetId: string): string {
    const palette = [
      '#4CAF50',
      '#2196F3',
      '#FF9800',
      '#E91E63',
      '#9C27B0',
      '#009688',
      '#607D8B',
      '#FF5722',
      '#3F51B5',
      '#795548',
    ];
    let h = 0;
    for (let i = 0; i < assetId.length; i++) {
      h = assetId.charCodeAt(i) + ((h << 5) - h);
    }
    return palette[Math.abs(h) % palette.length];
  }

  private applyEventDefaults(): void {
    if (this.isEditMode || this.eventId === '0') {
      return;
    }

    this.eventService
      .getEventDomain(this.eventId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((eventDomain) => {
        this.formGroup.patchValue({
          EventTypeId: eventDomain.EventTypeId || '',
          BannerId: eventDomain.Detail.BannerId || '',
          BannerName: eventDomain.Detail.BannerName || '',
          DivisionIds: eventDomain.DivisionIds || [],
        });
      });
  }

  getPrintAttributeTypeLabel(typeId: string | undefined): string {
    const id = String(typeId ?? '').trim();
    if (!id) {
      return '—';
    }
    const type = this.attributeTypeOptions.find((t) => t.id === id);
    return type ? type.label : id;
  }

  onImportSearchChange(): void {
    this.eventPrintAttributeService.importFromTemplateSearch(this.importSearch);
  }

  onCopyPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.eventPrintAttributeService.byNotEventIdPage(pageEvent);
  }

  isAllCopySelected(): boolean {
    return (
      this.copyAttributesList.length > 0 &&
      this.copyAttributesList.every((row) => this.selection.isSelected(row))
    );
  }

  isSomeCopySelectedOnPage(): boolean {
    return this.copyAttributesList.some((row) => this.selection.isSelected(row));
  }

  copyMasterToggle(): void {
    if (this.isAllCopySelected()) {
      this.copyAttributesList.forEach((row) => this.selection.deselect(row));
    } else {
      this.copyAttributesList.forEach((row) => {
        if (!this.selection.isSelected(row)) {
          this.selection.select(row);
        }
      });
    }
  }

  toggleCopySelect(checked: boolean, row: EventPrintAttributeDefinition): void {
    if (checked) {
      this.selection.select(row);
    } else {
      this.selection.deselect(row);
    }
  }

  submitLabel(): string {
    if (this.addMode === 'import') return 'Import';
    if (this.addMode === 'copy') return 'Copy Selected';
    return this.isEditMode ? 'Save' : 'Create';
  }

  switchAddMode(mode: 'create' | 'import' | 'copy'): void {
    if (this.addMode === 'copy' && mode !== 'copy') {
      this.copyAttributesSearchControl.reset('', { emitEvent: false });
      this.eventPrintAttributeService.ByNotEventIdSearch = '';
    }
    this.addMode = mode;
    if (mode === 'import') {
      this.importSelection.clear();
    }
    if (mode === 'copy') {
      this.selection.clear();
    }
  }

  clearCopyAttributesSearch(): void {
    this.copyAttributesSearchControl.setValue('');
  }

  onCopyEventSelectionChanged(eventId: string, evt: globalThis.Event): void {
    const input = evt.target as HTMLInputElement | null;
    const selected = !!input?.checked;
    if (!selected) {
      return;
    }
  }

  onCopyAttributeSelectionChanged(
    eventId: string,
    attributeId: string,
    evt: globalThis.Event,
  ): void {
    const input = evt.target as HTMLInputElement | null;
    const selected = !!input?.checked;
    this.copyEventOptions = this.copyEventOptions.map((option) => {
      if (option.id !== eventId) {
        return option;
      }
      return {
        ...option,
        attributes: option.attributes.map((a) =>
          a.id === attributeId ? { ...a, selected } : a,
        ),
      };
    });
  }

  toggleCopyEventSelectAll(eventId: string, checked: boolean): void {
    this.copyEventOptions = this.copyEventOptions.map((option) => {
      if (option.id !== eventId) {
        return option;
      }
      return {
        ...option,
        attributes: option.attributes.map((a) => ({ ...a, selected: checked })),
      };
    });
  }

  selectedCopyEvent():
    | {
        id: string;
        name: string;
        selected: boolean;
        attributes: Array<{
          id: string;
          name: string;
          type: string;
          divisionNames?: string;
          printPage?: string;
          selected: boolean;
        }>;
      }
    | undefined {
    return this.copyEventOptions.find((x) => x.selected);
  }

  selectedCopyAttributesCount(): number {
    return (this.selectedCopyEvent()?.attributes ?? []).filter((a) => a.selected).length;
  }

  isAllCopyAttributesSelected(): boolean {
    const attrs = this.selectedCopyEvent()?.attributes ?? [];
    return attrs.length > 0 && attrs.every((a) => a.selected);
  }

  copyAttributeBadgeStyle(typeLabel: string): { [key: string]: string } {
    const toneMap: Record<string, { bg: string; fg: string }> = {
      Address: { bg: '#dcedc8', fg: '#2e7d32' },
      'Sale Dates': { bg: '#e3f2fd', fg: '#1565c0' },
      URL: { bg: '#e1f5fe', fg: '#0277bd' },
      'Printer Code': { bg: '#ede7f6', fg: '#6a1b9a' },
      Header: { bg: '#ffebee', fg: '#c62828' },
      Creative: { bg: '#fce4ec', fg: '#ad1457' },
      'Market Place': { bg: '#e0f2f1', fg: '#006064' },
      'Offer Event': { bg: '#fff8e1', fg: '#c17900' },
      ABEV: { bg: '#efebe9', fg: '#795548' },
      'Dry Version': { bg: '#eceff1', fg: '#455a64' },
    };
    const tone = toneMap[typeLabel] ?? { bg: '#eceff1', fg: '#455a64' };
    return {
      background: tone.bg,
      color: tone.fg,
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      display: 'inline-block',
      marginRight: '8px',
    };
  }

  formatCopyDate(value?: string): string {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US');
  }

  private loadEventVersions(): void {
    if (!this.eventId || this.eventId === '0') {
      return;
    }

    this.versionService
      .getVersionsByEventId(this.eventId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((versions) => {
        this.availableVersions = versions || [];

        if (!this.isEditMode) {
          this.formGroup.get('VersionIds')?.setValue(this.availableVersions.map((v) => v.Id));
        }
      });
  }

  openEventsialog() {
    const dialogRef = this.dialog.open(UploadAssetsEventsComponent, {
      width: '60%',
      height: '90%',
      data: { eventId: this.eventId, includeExpiredEvents: true }
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          if (this.eventId === res.DomainId) {
            this.snackBar.open(
              'You cannot copy attributes from the same event.',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
            return;
          }
          this.sourceEvent = res;
          this.selection.clear();
          this.copySearchControl.setValue(this.sourceEvent.Detail.EventName, { emitEvent: false });
          this.copyAttributesSearchControl.reset('', { emitEvent: false });
          this.eventPrintAttributeService.ByNotEventIdSearch = '';
          this.eventPrintAttributeService.sourceEventId = this.sourceEvent.DomainId;
          this.eventPrintAttributeService.targetEventId = this.eventId;
          this.eventPrintAttributeService.byNotEventIdPage({
            ...this.eventPrintAttributeService.ByNotEventIdPage,
            pageIndex: 0,
          });
          this.eventPrintAttributeService.byNotEventIdReload();
        }
      });
  }

  onImportPage(pageEvent: PageEvent): void {
    this.eventPrintAttributeService.importFromTemplatePage(pageEvent);
  }

  isAllImportSelected(): boolean {
    return this.importSelection.selected.length === this.templatesList.length;
  }

  importMasterToggle() {
    if (this.isAllImportSelected()) {
      this.importSelection.clear();
    } else {
      this.templatesList.forEach((row) => this.importSelection.select(row));
    }
  }

  toggleImportSelect(checked: boolean, row: EventPrintAttributeDefinition): void {
    if (checked) {
      this.importSelection.select(row);
    } else {
      this.importSelection.deselect(row);
    }
  }

  clearImportSearch(): void {
    this.importSearch = '';
    this.eventPrintAttributeService.importFromTemplateSearch('');
  }

  canEdit() {
    if(this.readOnlyMode) {
      this.formGroup.disable();
      this.detailForm.disable();
    }
    return !this.readOnlyMode;
  }
}
